import { useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Minus, X, Package, Clock, AlertCircle } from 'lucide-react';
import { LoadingButton } from '@/components/LoadingButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOrgs } from '@/contexts/OrgsContext';
import { useRoles } from '@/contexts/RolesContext';
import {
  useGetOrgProductSubscription,
  useGetOrgSubscriptionAvailableAddOns,
  usePreviewOrgSubscriptionChange,
  useApplyOrgSubscriptionChanges,
  getGetOrgProductSubscriptionQueryKey,
  getGetOrgSubscriptionAvailableAddOnsQueryKey,
} from '@/api/auth/billing/billing';
import { getGetOrganizationProductEntitlementsQueryKey } from '@/api/auth/product-entitlements/product-entitlements';
import type {
  SubscriptionLineItemResponse,
  AvailableAddOn,
  PreviewSubscriptionChangeResponse,
  ProposedLineItem,
} from '@/api/auth/missionImpactAuthAPI.schemas';
import { useError } from '@/contexts/ErrorContext';
import { formatMinorUnitsAmount } from '@/lib/formatCurrency';

/**
 * Represents a pending change to an add-on
 */
interface PendingChange {
  priceId: string;
  itemId?: string;
  originalQuantity?: number;
  newQuantity: number;
  action: 'add' | 'update' | 'remove';
}

/**
 * Props for ManageAddOnsDialog
 */
interface ManageAddOnsDialogProps {
  /** Whether the organization has an active Stripe subscription */
  hasStripeCustomer: boolean;
}

/**
 * Dialog for managing subscription add-ons.
 * Allows users to add, remove, or update quantities of add-ons with a preview step.
 */
export function ManageAddOnsDialog({ hasStripeCustomer }: ManageAddOnsDialogProps) {
  const { t, language } = useLanguage();
  const { handleApiError } = useError();
  const { selectedOrganizationId } = useOrgs();
  const { isAdmin, isBillingManager } = useRoles();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'edit' | 'preview'>('edit');
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());
  const [previewData, setPreviewData] = useState<PreviewSubscriptionChangeResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canManageAddOns = (isAdmin || isBillingManager) && hasStripeCustomer;

  // Request options with Accept-Language header for localized responses
  const requestOptions = { headers: { 'Accept-Language': language } };

  // Fetch current subscription for product
  const {
    data: subscriptionResponse,
    isLoading: isLoadingSubscription,
  } = useGetOrgProductSubscription(selectedOrganizationId ?? '', 'data-mission', {
    query: {
      enabled: !!selectedOrganizationId && open && canManageAddOns,
    },
    request: requestOptions,
  });

  const subscription = subscriptionResponse?.data;
  const subscriptionId = subscription?.subscriptionId;
  const isCancellationScheduled = subscription?.cancelAtPeriodEnd ?? false;

  // Fetch available add-ons for subscription
  const {
    data: addOnsResponse,
    isLoading: isLoadingAddOns,
  } = useGetOrgSubscriptionAvailableAddOns(selectedOrganizationId ?? '', subscriptionId ?? '', {
    query: {
      enabled: !!selectedOrganizationId && !!subscriptionId && open && canManageAddOns,
    },
    request: requestOptions,
  });

  const availableAddOns = addOnsResponse?.data.addOns ?? [];

  // Mutations
  const previewMutation = usePreviewOrgSubscriptionChange({ request: requestOptions });
  const applyMutation = useApplyOrgSubscriptionChanges({ request: requestOptions });

  const isLoading = isLoadingSubscription || isLoadingAddOns;

  // Get current add-on items from subscription
  const currentAddOns = useMemo(() => {
    return subscription?.items.filter((item) => item.isAddOn) ?? [];
  }, [subscription]);

  // Get available add-ons that are not already subscribed
  const purchasableAddOns = useMemo(() => {
    return availableAddOns.filter((addOn) => !addOn.alreadySubscribed);
  }, [availableAddOns]);

  /**
   * Format currency amount for display (converts from cents to dollars)
   */
  const formatAmount = useCallback(
    (amount: number, currency: string) => formatMinorUnitsAmount(amount, currency, language),
    [language]
  );

  /**
   * Get the interval suffix for display
   */
  const getIntervalSuffix = useCallback(
    (interval: string) => {
      if (interval === 'year') return t.billing.manageAddOns.perYear;
      return t.billing.manageAddOns.perMonth;
    },
    [t]
  );

  /**
   * Get the effective quantity for an item (considering pending changes)
   */
  const getEffectiveQuantity = useCallback(
    (priceId: string, currentQuantity: number) => {
      const change = pendingChanges.get(priceId);
      if (!change) return currentQuantity;
      if (change.action === 'remove') return 0;
      return change.newQuantity;
    },
    [pendingChanges]
  );

  /**
   * Check if there are any pending changes
   */
  const hasChanges = useMemo(() => {
    return pendingChanges.size > 0;
  }, [pendingChanges]);

  /**
   * Get the baseline quantity for an item (scheduledQuantity if set, otherwise quantity)
   */
  const getBaselineQuantity = useCallback((item: SubscriptionLineItemResponse) => {
    return item.scheduledQuantity ?? item.quantity;
  }, []);

  /**
   * Handle quantity change for existing add-on
   */
  const handleQuantityChange = useCallback(
    (item: SubscriptionLineItemResponse, delta: number) => {
      const baselineQuantity = getBaselineQuantity(item);
      const currentEffective = getEffectiveQuantity(item.priceId, baselineQuantity);
      const newQuantity = Math.max(0, currentEffective + delta);

      setPendingChanges((prev) => {
        const next = new Map(prev);

        if (newQuantity === 0) {
          // Remove the item
          next.set(item.priceId, {
            priceId: item.priceId,
            itemId: item.itemId,
            originalQuantity: baselineQuantity,
            newQuantity: 0,
            action: 'remove',
          });
        } else if (newQuantity === baselineQuantity) {
          // No change from baseline
          next.delete(item.priceId);
        } else {
          // Update quantity
          next.set(item.priceId, {
            priceId: item.priceId,
            itemId: item.itemId,
            originalQuantity: baselineQuantity,
            newQuantity,
            action: 'update',
          });
        }

        return next;
      });
    },
    [getEffectiveQuantity, getBaselineQuantity]
  );

  /**
   * Handle adding a new add-on
   */
  const handleAddAddOn = useCallback((addOn: AvailableAddOn) => {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      const existing = next.get(addOn.priceId);

      if (existing) {
        // Increment quantity if already added
        next.set(addOn.priceId, {
          ...existing,
          newQuantity: existing.newQuantity + 1,
        });
      } else {
        // Add new
        next.set(addOn.priceId, {
          priceId: addOn.priceId,
          newQuantity: 1,
          action: 'add',
        });
      }

      return next;
    });
  }, []);

  /**
   * Handle removing a pending new add-on
   */
  const handleRemovePendingAddOn = useCallback((priceId: string) => {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.delete(priceId);
      return next;
    });
  }, []);

  /**
   * Handle removing an existing add-on
   */
  const handleRemoveExisting = useCallback(
    (item: SubscriptionLineItemResponse) => {
      const baselineQuantity = getBaselineQuantity(item);
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.set(item.priceId, {
          priceId: item.priceId,
          itemId: item.itemId,
          originalQuantity: baselineQuantity,
          newQuantity: 0,
          action: 'remove',
        });
        return next;
      });
    },
    [getBaselineQuantity]
  );

  /**
   * Build proposed line items for preview
   */
  const buildProposedItems = useCallback((): ProposedLineItem[] => {
    if (!subscription) return [];

    // Include all current non-addon items (base subscription)
    const baseItems = subscription.items
      .filter((item) => !item.isAddOn)
      .map((item) => ({ priceId: item.priceId, quantity: item.scheduledQuantity ?? item.quantity }));

    // Include all add-ons with their final quantities
    const addOnItems: ProposedLineItem[] = [];

    // Current add-ons with changes (include removed items with quantity 0)
    subscription.items
      .filter((item) => item.isAddOn)
      .forEach((item) => {
        const baselineQuantity = item.scheduledQuantity ?? item.quantity;
        const change = pendingChanges.get(item.priceId);
        if (change?.action === 'remove') {
          addOnItems.push({ priceId: item.priceId, quantity: 0 });
        } else {
          const quantity = change?.newQuantity ?? baselineQuantity;
          addOnItems.push({ priceId: item.priceId, quantity });
        }
      });

    // New add-ons
    pendingChanges.forEach((change) => {
      if (change.action === 'add' && change.newQuantity > 0) {
        addOnItems.push({ priceId: change.priceId, quantity: change.newQuantity });
      }
    });

    return [...baseItems, ...addOnItems];
  }, [subscription, pendingChanges]);

  /**
   * Handle dialog close
   */
  const handleClose = useCallback(() => {
    setOpen(false);
    setStep('edit');
    setPendingChanges(new Map());
    setPreviewData(null);
  }, []);

  /**
   * Handle preview button click
   */
  const handlePreview = useCallback(async () => {
    if (!selectedOrganizationId || !subscriptionId || !hasChanges) return;

    try {
      const proposedItems = buildProposedItems();
      const result = await previewMutation.mutateAsync({
        orgId: selectedOrganizationId,
        subscriptionId,
        data: { items: proposedItems },
      });

      setPreviewData(result.data);
      setStep('preview');
    } catch (error) {
      handleApiError(error, { context: 'ManageAddOnsDialog.preview' }, { showToast: false });
      toast.error(t.billing.manageAddOns.errorPreview);
    }
  }, [selectedOrganizationId, subscriptionId, hasChanges, buildProposedItems, previewMutation, t]);

  /**
   * Handle confirm button click - apply all changes
   */
  const handleConfirm = useCallback(async () => {
    if (!selectedOrganizationId || !subscriptionId) return;

    setIsSubmitting(true);

    try {
      // Apply all changes in a single request
      const proposedItems = buildProposedItems();
      await applyMutation.mutateAsync({
        orgId: selectedOrganizationId,
        subscriptionId,
        data: { items: proposedItems },
      });

      // Invalidate queries
      await queryClient.invalidateQueries({
        queryKey: getGetOrgProductSubscriptionQueryKey(selectedOrganizationId, 'data-mission'),
      });
      await queryClient.invalidateQueries({
        queryKey: getGetOrgSubscriptionAvailableAddOnsQueryKey(selectedOrganizationId, subscriptionId),
      });
      await queryClient.invalidateQueries({
        queryKey: getGetOrganizationProductEntitlementsQueryKey(selectedOrganizationId, 'data-mission'),
      });

      toast.success(t.billing.manageAddOns.success);
      handleClose();
    } catch (error) {
      handleApiError(error, { context: 'ManageAddOnsDialog.confirm' }, { showToast: false });
      toast.error(t.billing.manageAddOns.errorUpdate);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedOrganizationId,
    subscriptionId,
    buildProposedItems,
    applyMutation,
    queryClient,
    t,
    handleClose,
  ]);

  /**
   * Handle dialog open change
   */
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isSubmitting) {
        handleClose();
      } else {
        setOpen(newOpen);
      }
    },
    [isSubmitting, handleClose]
  );

  /**
   * Handle back button in preview mode
   */
  const handleBack = useCallback(() => {
    setStep('edit');
    setPreviewData(null);
  }, []);

  if (!canManageAddOns) {
    return null;
  }

  /**
   * Render loading skeleton
   */
  const renderSkeleton = () => (
    <div className="space-y-4">
      <Skeleton className="h-6 w-32" />
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
  );

  /**
   * Format the pending changes effective date
   */
  const formatPendingChangesDate = useCallback(() => {
    if (!subscription?.pendingChangesEffectiveDate) return '';
    return new Date(subscription.pendingChangesEffectiveDate).toLocaleDateString(language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [subscription?.pendingChangesEffectiveDate, language]);

  /**
   * Render edit mode content
   */
  const renderEditMode = () => {
    const pendingNewAddOns = Array.from(pendingChanges.values()).filter(
      (change) => change.action === 'add'
    );

    // Available add-ons not yet added
    const notYetAdded = purchasableAddOns.filter((addOn) => !pendingChanges.has(addOn.priceId));

    const hasAnyItems = currentAddOns.length > 0 || pendingNewAddOns.length > 0 || notYetAdded.length > 0;

    return (
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {/* Cancellation scheduled alert */}
        {isCancellationScheduled && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{t.billing.manageAddOns.cancellationScheduled}</span>
          </div>
        )}

        {/* Pending changes banner */}
        {!isCancellationScheduled && subscription?.hasPendingChanges && subscription.pendingChangesEffectiveDate && (
          <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50 text-sm text-muted-foreground">
            <Clock className="h-4 w-4 flex-shrink-0" />
            <span>
              {t.billing.manageAddOns.pendingChanges.replace('{date}', formatPendingChangesDate())}
            </span>
          </div>
        )}

        {!hasAnyItems ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t.billing.manageAddOns.noAvailableAddOns}
          </p>
        ) : (
          <>
            {/* Existing add-ons */}
            {currentAddOns.map((item) => {
              const change = pendingChanges.get(item.priceId);
              const isRemoved = change?.action === 'remove';
              const baselineQuantity = getBaselineQuantity(item);
              const effectiveQuantity = getEffectiveQuantity(item.priceId, baselineQuantity);

              return (
                <div
                  key={item.itemId}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isRemoved ? 'bg-destructive/10 border-destructive/20' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${isRemoved ? 'line-through text-muted-foreground' : ''}`}>
                      {item.productName}
                    </p>
                    {item.description && (
                      <p className={`text-sm text-muted-foreground ${isRemoved ? 'line-through' : ''}`}>
                        {item.description}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {formatAmount(item.unitAmount, subscription?.currency ?? 'USD')}
                    </p>
                  </div>

                  {!isRemoved && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => { handleQuantityChange(item, -1); }}
                        disabled={isCancellationScheduled || effectiveQuantity <= 0}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{effectiveQuantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => { handleQuantityChange(item, 1); }}
                        disabled={isCancellationScheduled}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      {item.canRemove && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => { handleRemoveExisting(item); }}
                          disabled={isCancellationScheduled}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}

                  {isRemoved && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { handleRemovePendingAddOn(item.priceId); }}
                      disabled={isCancellationScheduled}
                    >
                      Undo
                    </Button>
                  )}
                </div>
              );
            })}

            {/* Pending new add-ons (added but not yet saved) */}
            {pendingNewAddOns.map((change) => {
              const addOn = availableAddOns.find((a) => a.priceId === change.priceId);
              if (!addOn) return null;

              return (
                <div
                  key={change.priceId}
                  className="flex items-center justify-between p-3 rounded-lg border bg-primary/5 border-primary/20"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{addOn.productName}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatAmount(addOn.unitAmount, addOn.currency)}
                      {getIntervalSuffix(addOn.interval)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={isCancellationScheduled}
                      onClick={() => {
                        if (change.newQuantity <= 1) {
                          handleRemovePendingAddOn(change.priceId);
                        } else {
                          setPendingChanges((prev) => {
                            const next = new Map(prev);
                            next.set(change.priceId, {
                              ...change,
                              newQuantity: change.newQuantity - 1,
                            });
                            return next;
                          });
                        }
                      }}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-medium">{change.newQuantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={isCancellationScheduled}
                      onClick={() => {
                        setPendingChanges((prev) => {
                          const next = new Map(prev);
                          next.set(change.priceId, {
                            ...change,
                            newQuantity: change.newQuantity + 1,
                          });
                          return next;
                        });
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      disabled={isCancellationScheduled}
                      onClick={() => { handleRemovePendingAddOn(change.priceId); }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}

            {/* Available add-ons (not yet added, quantity = 0) */}
            {notYetAdded.map((addOn) => (
              <div
                key={addOn.priceId}
                className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{addOn.productName}</p>
                  {addOn.description && (
                    <p className="text-sm text-muted-foreground">{addOn.description}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {formatAmount(addOn.unitAmount, addOn.currency)}
                    {getIntervalSuffix(addOn.interval)}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="w-8 text-center font-medium text-muted-foreground">0</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => { handleAddAddOn(addOn); }}
                    disabled={isCancellationScheduled}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  /**
   * Render preview mode content
   */
  const renderPreviewMode = () => {
    if (!previewData) return null;

    // Separate prorated (immediate) charges from recurring charges
    const proratedItems = previewData.lineItems.filter((item) => item.isProration);
    const recurringItems = previewData.lineItems.filter((item) => !item.isProration);

    return (
      <div className="space-y-6">
        {/* Due Today - Prorated charges */}
        {proratedItems.length > 0 && (
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium">{t.billing.manageAddOns.preview.dueToday}</h4>
              <p className="text-xs text-muted-foreground">
                {t.billing.manageAddOns.preview.dueTodayDescription}
              </p>
            </div>

            <div className="space-y-2">
              {proratedItems.map((item, index) => (
                <div
                  key={`prorated-${String(index)}`}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                >
                  <div className="flex-1">
                    <p className="font-medium">
                      {item.productName} x {item.quantity ?? 1}
                    </p>
                  </div>
                  <p className="font-medium">
                    {formatAmount(item.totalAmount, previewData.currency)}
                  </p>
                </div>
              ))}
            </div>

            {/* Immediate charge total */}
            {previewData.immediateCharge > 0 && (
              <div className="flex justify-between p-4 rounded-lg bg-muted/50">
                <span className="text-muted-foreground">
                  {t.billing.manageAddOns.preview.immediateCharge}
                </span>
                <span className="font-medium">
                  {formatAmount(previewData.immediateCharge, previewData.currency)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Next Renewal - Recurring charges */}
        {recurringItems.length > 0 && (
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium">{t.billing.manageAddOns.preview.nextRenewal}</h4>
              <p className="text-xs text-muted-foreground">
                {t.billing.manageAddOns.preview.nextRenewalDescription}
              </p>
            </div>

            <div className="space-y-2">
              {recurringItems.map((item, index) => (
                <div
                  key={`recurring-${String(index)}`}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                >
                  <div className="flex-1">
                    <p className="font-medium">
                      {item.productName}
                      {item.quantity != null && item.quantity > 1 && ` x ${String(item.quantity)}`}
                    </p>
                  </div>
                  <p className="font-medium">
                    {formatAmount(item.totalAmount, previewData.currency)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary Totals */}
        <div className="space-y-2 p-4 rounded-lg bg-muted/50">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t.billing.manageAddOns.preview.effectiveDate}
            </span>
            <span className="font-medium">
              {new Date(previewData.effectiveDate).toLocaleDateString(language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t.billing.manageAddOns.preview.newRecurringTotal}
            </span>
            <span className="font-semibold">
              {formatAmount(previewData.newRecurringTotal, previewData.currency)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Package className="h-4 w-4 mr-2" />
          {t.billing.manageAddOns.button}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {step === 'edit' ? t.billing.manageAddOns.title : t.billing.manageAddOns.preview.title}
          </DialogTitle>
          <DialogDescription>
            {step === 'edit'
              ? t.billing.manageAddOns.description
              : t.billing.manageAddOns.preview.description}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? renderSkeleton() : step === 'edit' ? renderEditMode() : renderPreviewMode()}

        <DialogFooter className="flex justify-end gap-2">
          {step === 'edit' ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                {t.wizard.common.cancel}
              </Button>
              <LoadingButton
                onClick={() => void handlePreview()}
                disabled={isCancellationScheduled || !hasChanges}
                isLoading={previewMutation.isPending}
              >
                {t.billing.manageAddOns.previewChanges}
              </LoadingButton>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
                {t.billing.manageAddOns.back}
              </Button>
              <LoadingButton onClick={() => void handleConfirm()} isLoading={isSubmitting}>
                {t.billing.manageAddOns.confirmChanges}
              </LoadingButton>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
